/**
 * OmegaRichMessage — shared rich markdown renderer for all Omega chat surfaces.
 * Handles: headers (H1–H4), tables, code blocks, blockquotes, lists, bold/italic/inline-code,
 * horizontal rules, chart placeholders, and status icons (✅/❌/⚠).
 * No external markdown library — pure React.
 */
import React, { useRef, useMemo } from "react";
import { Bar, Line } from "react-chartjs-2";

// ─── Chart Block ─────────────────────────────────────────────────────────────
export function ChartBlock({ spec }) {
  const chartRef = useRef(null);
  const { type, title, labels = [], series = [], xLabel, yLabel } = spec || {};
  const safeType = type === "bar" ? "bar" : "line";
  const COLORS = ["#38bdf8", "#a78bfa", "#4ade80", "#fbbf24", "#f87171"];

  const data = useMemo(() => ({
    labels: labels.slice(0, 60),
    datasets: (series || []).slice(0, 5).map((s, i) => ({
      label: s.label || `Series ${i + 1}`,
      data: (s.data || []).slice(0, 60).map(Number),
      borderColor: s.color || COLORS[i % COLORS.length],
      backgroundColor: (s.color || COLORS[i % COLORS.length]) + (safeType === "bar" ? "BB" : "22"),
      fill: safeType === "line", tension: 0.35, borderWidth: 2, pointRadius: 0,
    })),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [labels, series, safeType]);

  const options = useMemo(() => ({
    responsive: true, maintainAspectRatio: false,
    plugins: {
      legend: { display: (series || []).length > 1, labels: { color: "var(--muted)", font: { size: 10 } } },
      tooltip: { mode: "index", intersect: false, backgroundColor: "#0f172a", titleColor: "#e2e8f0", bodyColor: "#94a3b8" },
    },
    scales: {
      x: {
        title: xLabel ? { display: true, text: xLabel, color: "var(--dim)", font: { size: 10 } } : { display: false },
        ticks: { color: "var(--dim)", font: { size: 9 }, maxTicksLimit: 7 },
        grid: { color: "rgba(255,255,255,0.05)" },
      },
      y: {
        title: yLabel ? { display: true, text: yLabel, color: "var(--dim)", font: { size: 10 } } : { display: false },
        ticks: { color: "var(--dim)", font: { size: 9 } },
        grid: { color: "rgba(255,255,255,0.05)" },
      },
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [series, xLabel, yLabel]);

  const savePNG = () => {
    try {
      const url = chartRef.current?.toBase64Image("image/png", 1.0);
      if (!url) return;
      const a = document.createElement("a");
      a.href = url; a.download = `omega-chart-${Date.now()}.png`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch {}
  };

  const ChartComp = safeType === "bar" ? Bar : Line;
  return (
    <div data-testid="zi-chat-chart" style={{
      margin: "10px 0", padding: "12px 14px",
      background: "rgba(15,23,42,0.6)", border: "1px solid rgba(96,165,250,0.22)",
      borderRadius: 14,
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.2, textTransform: "uppercase", color: "#a5b4fc" }}>
          {title || "Omega Chart"}
        </div>
        <button
          data-testid="zi-chat-chart-save"
          onClick={savePNG}
          style={{ background: "rgba(96,165,250,0.14)", border: "1px solid rgba(96,165,250,0.35)", color: "#60a5fa", fontSize: 10, fontWeight: 700, padding: "4px 9px", borderRadius: 8, cursor: "pointer", fontFamily: "inherit" }}
        >SAVE PNG</button>
      </div>
      <div style={{ height: 190 }}>
        <ChartComp ref={chartRef} data={data} options={options} />
      </div>
    </div>
  );
}

// ─── Inline parser: **bold**, *italic*, `code` ────────────────────────────────
function parseInline(str) {
  if (!str) return str;
  const parts = [];
  // Order matters: ** before * so bold isn't consumed as two italics
  const RE = /\*\*(.+?)\*\*|`([^`\n]+)`|\*([^*\n]+)\*/g;
  let last = 0, m;
  while ((m = RE.exec(str)) !== null) {
    if (m.index > last) parts.push(<span key={parts.length}>{str.slice(last, m.index)}</span>);
    if (m[1] !== undefined) {
      parts.push(<strong key={parts.length} style={{ fontWeight: 700, color: "inherit" }}>{m[1]}</strong>);
    } else if (m[2] !== undefined) {
      parts.push(
        <code key={parts.length} style={{
          background: "rgba(96,165,250,0.13)", padding: "1px 5px", borderRadius: 4,
          fontSize: "0.87em", fontFamily: "monospace", color: "#93c5fd",
        }}>{m[2]}</code>
      );
    } else if (m[3] !== undefined) {
      parts.push(<em key={parts.length} style={{ opacity: 0.85, fontStyle: "italic" }}>{m[3]}</em>);
    }
    last = m.index + m[0].length;
  }
  if (last < str.length) parts.push(<span key={parts.length}>{str.slice(last)}</span>);
  if (!parts.length) return str;
  if (parts.length === 1) return parts[0];
  return <>{parts}</>;
}

// ─── Table separator row detection `| --- | --- |` ───────────────────────────
function isTableSep(line) {
  const t = line.trim();
  if (!t.startsWith("|")) return false;
  return t.split("|").slice(1, -1).every(c => /^[\s\-:]+$/.test(c));
}

// ─── Status-aware cell renderer ───────────────────────────────────────────────
function Cell({ text }) {
  const t = (text || "").trim();
  if (/^✅/.test(t)) return <span style={{ color: "#4ade80", fontWeight: 600 }}>{parseInline(t)}</span>;
  if (/^❌/.test(t)) return <span style={{ color: "#f87171", fontWeight: 600 }}>{parseInline(t)}</span>;
  if (/^⚠/.test(t))  return <span style={{ color: "#fbbf24", fontWeight: 600 }}>{parseInline(t)}</span>;
  return <>{parseInline(t)}</>;
}

// ─── Main block renderer ──────────────────────────────────────────────────────
function renderBlocks(text, charts) {
  if (!text && !charts?.length) return null;
  const lines = (text || "").split("\n");
  const blocks = [];
  let i = 0;

  while (i < lines.length) {
    const raw = lines[i];
    const line = raw.trim();

    // Empty line — skip
    if (!line) { i++; continue; }

    // ── Chart placeholder [CHART::N]
    const chartM = line.match(/^\[CHART::(\d+)\]$/);
    if (chartM) {
      const idx = parseInt(chartM[1], 10);
      const c = charts?.[idx];
      if (c) blocks.push(<ChartBlock key={`chart-${i}`} spec={c.spec || c} />);
      i++; continue;
    }

    // ── Fenced code block ```
    if (line.startsWith("```")) {
      const lang = line.slice(3).trim();
      const codeLines = [];
      i++;
      while (i < lines.length && !lines[i].trim().startsWith("```")) {
        codeLines.push(lines[i]);
        i++;
      }
      i++; // skip closing ```
      // Don't render chart/followups fences as code (they're stripped upstream, but just in case)
      if (lang === "chart" || lang === "followups") continue;
      blocks.push(
        <pre key={`code-${blocks.length}`} style={{
          margin: "8px 0", padding: "10px 14px",
          background: "rgba(10,15,30,0.85)", border: "1px solid rgba(255,255,255,0.07)",
          borderRadius: 10, overflowX: "auto",
          fontSize: 11.5, fontFamily: "'JetBrains Mono', 'Fira Code', 'Courier New', monospace",
          color: "#cbd5e1", lineHeight: 1.65, whiteSpace: "pre",
        }}>{codeLines.join("\n")}</pre>
      );
      continue;
    }

    // ── H1
    if (line.startsWith("# ") && !line.startsWith("## ")) {
      blocks.push(
        <div key={`h1-${i}`} style={{
          fontSize: 16, fontWeight: 800, margin: "14px 0 6px",
          color: "var(--text)", borderBottom: "2px solid rgba(167,139,250,0.35)",
          paddingBottom: 5, lineHeight: 1.3,
        }}>{parseInline(line.slice(2))}</div>
      );
      i++; continue;
    }

    // ── H2
    if (line.startsWith("## ") && !line.startsWith("### ")) {
      blocks.push(
        <div key={`h2-${i}`} style={{
          fontSize: 13.5, fontWeight: 800, margin: "12px 0 4px",
          color: "#a78bfa", letterSpacing: 0.3, lineHeight: 1.4,
        }}>{parseInline(line.slice(3))}</div>
      );
      i++; continue;
    }

    // ── H3
    if (line.startsWith("### ")) {
      blocks.push(
        <div key={`h3-${i}`} style={{
          fontSize: 12.5, fontWeight: 700, margin: "10px 0 3px",
          color: "#38bdf8", lineHeight: 1.4,
        }}>{parseInline(line.slice(4))}</div>
      );
      i++; continue;
    }

    // ── H4
    if (line.startsWith("#### ")) {
      blocks.push(
        <div key={`h4-${i}`} style={{
          fontSize: 11.5, fontWeight: 700, margin: "8px 0 2px",
          color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.6,
        }}>{parseInline(line.slice(5))}</div>
      );
      i++; continue;
    }

    // ── Horizontal rule --- or === or ***
    if (/^[-=*]{3,}$/.test(line)) {
      blocks.push(
        <div key={`hr-${i}`} style={{
          height: 1, margin: "10px 0",
          background: "linear-gradient(90deg, rgba(167,139,250,0.45), rgba(167,139,250,0.08), transparent)",
        }} />
      );
      i++; continue;
    }

    // ── Blockquote >
    if (line.startsWith("> ")) {
      const qLines = [];
      while (i < lines.length && lines[i].trim().startsWith("> ")) {
        qLines.push(lines[i].trim().slice(2));
        i++;
      }
      const combined = qLines.join(" ");
      const isWarn    = /[⚠]/.test(combined) || /\bWARN|CAUTION|FLAGGED\b/i.test(combined);
      const isSuccess = /[✅]/.test(combined) || /\bAPPROVED|PASS\b/i.test(combined);
      const isError   = /[❌]/.test(combined) || /\bFAIL|BLOCK\b/i.test(combined);
      const accent = isError ? "#f87171" : isWarn ? "#fbbf24" : isSuccess ? "#4ade80" : "#a78bfa";
      blocks.push(
        <div key={`bq-${blocks.length}`} style={{
          borderLeft: `3px solid ${accent}`,
          background: `${accent}0d`,
          borderRadius: "0 8px 8px 0",
          padding: "8px 12px", margin: "6px 0",
        }}>
          {qLines.map((l, j) => (
            <div key={j} style={{ fontSize: 13, lineHeight: 1.6, color: "var(--text)" }}>
              {parseInline(l)}
            </div>
          ))}
        </div>
      );
      continue;
    }

    // ── Table | ... |
    if (line.startsWith("|")) {
      const tableLines = [];
      while (i < lines.length && lines[i].trim().startsWith("|")) {
        tableLines.push(lines[i].trim());
        i++;
      }
      const nonSep = tableLines.filter(l => !isTableSep(l));
      if (nonSep.length >= 1) {
        const [hdr, ...rows] = nonSep;
        const hCells = hdr.split("|").slice(1, -1).map(c => c.trim());
        blocks.push(
          <div key={`tbl-${blocks.length}`} style={{ overflowX: "auto", margin: "8px 0", borderRadius: 10, border: "1px solid rgba(255,255,255,0.07)" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: rows.length ? undefined : "100%" }}>
              <thead>
                <tr style={{ background: "rgba(167,139,250,0.1)" }}>
                  {hCells.map((c, j) => (
                    <th key={j} style={{
                      padding: "7px 11px", textAlign: "left",
                      color: "#c4b5fd", fontWeight: 700, fontSize: 10.5,
                      letterSpacing: 0.5, whiteSpace: "nowrap",
                      borderBottom: "1px solid rgba(167,139,250,0.2)",
                    }}>{parseInline(c)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, ri) => {
                  const cells = row.split("|").slice(1, -1).map(c => c.trim());
                  return (
                    <tr key={ri} style={{ background: ri % 2 === 0 ? "transparent" : "rgba(255,255,255,0.02)" }}>
                      {cells.map((c, j) => (
                        <td key={j} style={{
                          padding: "5px 11px", color: "var(--text)", fontSize: 12,
                          borderBottom: ri < rows.length - 1 ? "1px solid rgba(255,255,255,0.04)" : "none",
                          lineHeight: 1.45,
                        }}><Cell text={c} /></td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      }
      continue;
    }

    // ── Unordered or ordered list
    if (/^(\s*[-*+]|\d+\.) /.test(line)) {
      const items = [];
      const ordered = /^\d+\./.test(line);
      while (i < lines.length && /^\s*([-*+]|\d+\.) /.test(lines[i].trim())) {
        const indent = (lines[i].match(/^(\s*)/)?.[1] || "").length;
        items.push({ text: lines[i].trim().replace(/^[-*+]\s+/, "").replace(/^\d+\.\s+/, ""), indent });
        i++;
      }
      const Tag = ordered ? "ol" : "ul";
      blocks.push(
        <Tag key={`list-${blocks.length}`} style={{
          margin: "5px 0 5px 14px", padding: 0,
          display: "flex", flexDirection: "column", gap: 2,
          listStyleType: ordered ? "decimal" : "disc",
        }}>
          {items.map((item, j) => (
            <li key={j} style={{ fontSize: 13, lineHeight: 1.55, color: "var(--text)", paddingLeft: item.indent > 0 ? item.indent * 8 : 0 }}>
              {parseInline(item.text)}
            </li>
          ))}
        </Tag>
      );
      continue;
    }

    // ── Plain paragraph / line
    blocks.push(
      <div key={`p-${i}`} style={{ fontSize: 13, lineHeight: 1.65, margin: "1px 0", color: "var(--text)" }}>
        {parseInline(raw)}
      </div>
    );
    i++;
  }

  return blocks.length ? blocks : null;
}

// ─── Main export ──────────────────────────────────────────────────────────────
export default function OmegaRichMessage({ content, charts }) {
  if (!content) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {renderBlocks(content, charts || [])}
    </div>
  );
}
